import React, { useState } from 'react';
import axios from 'axios';
import { Warning, X } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;

const REASONS = [
  { id: 'inappropriate_content', label: 'Inappropriate Content' },
  { id: 'spam', label: 'Spam' },
  { id: 'harassment', label: 'Harassment / Bullying' },
  { id: 'underage', label: 'Underage Content' },
  { id: 'non_consensual', label: 'Non-Consensual Content' },
  { id: 'impersonation', label: 'Impersonation' },
  { id: 'other', label: 'Other' },
];

const ReportModal = ({ open, onOpenChange, targetType, targetId, targetName }) => {
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!reason) {
      toast.error('Please select a reason');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/api/moderation/report`,
        {
          target_type: targetType,
          target_id: targetId,
          reason,
          description,
        },
        { withCredentials: true }
      );
      toast.success('Report submitted. Our team will review it.');
      onOpenChange(false);
      setReason('');
      setDescription('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-white/10 text-white max-w-md" aria-describedby="report-description">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Warning weight="fill" className="w-5 h-5 text-amber-400" />
            Report {targetType === 'content' ? 'Content' : 'User'}
          </DialogTitle>
          <p id="report-description" className="text-sm text-slate-400">
            {targetName ? `Reporting: ${targetName}` : 'Help us keep the community safe'}
          </p>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Reason Selection */}
          <div>
            <p className="text-sm font-medium text-slate-300 mb-2">Why are you reporting this?</p>
            <div className="space-y-2">
              {REASONS.map((r) => (
                <button
                  key={r.id}
                  onClick={() => setReason(r.id)}
                  className={`w-full text-left px-4 py-3 rounded-xl border transition-all text-sm ${
                    reason === r.id
                      ? 'border-blue-500 bg-blue-500/10 text-white'
                      : 'border-white/10 bg-slate-800/50 text-slate-300 hover:border-white/20'
                  }`}
                  data-testid={`report-reason-${r.id}`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>

          {/* Description */}
          <div>
            <p className="text-sm font-medium text-slate-300 mb-2">Additional details (optional)</p>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Provide more context about your report..."
              className="w-full bg-slate-800 border border-white/10 rounded-xl px-4 py-3 text-white placeholder-slate-500 text-sm resize-none h-20 focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500"
              data-testid="report-description-input"
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              onClick={() => onOpenChange(false)}
              className="flex-1 py-3 border border-white/10 text-white rounded-full font-medium hover:bg-white/5"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={submitting || !reason}
              className="flex-1 py-3 bg-blue-500 text-white rounded-full font-semibold hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="report-submit-btn"
            >
              {submitting ? 'Submitting...' : 'Submit Report'}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReportModal;
